/**
 * Welcome-back composer — turns the overnight tick's ground-truth Beats into a
 * small set of warm letters (CLAUDE.md rule #8: "letters, not a dashboard").
 *
 * Phrasing goes through the TemplateVoice FLOOR (zero tokens, always available, warm
 * by construction) so a returning player is greeted gently and reliably even with
 * the LLM off/offline. The LLM can re-voice the live town once they're in; the
 * welcome-back itself stays cheap and instant. Never a backlog: capped + soft.
 */
import type { Beat, SimTrainer, Voice, SpeakerContext, VoiceLine } from '@/shared';
import type { WelcomeLetter } from './types.js';

/** Cap on letters surfaced — a lovely handful, never an overwhelming wall. */
export const MAX_WELCOME_LETTERS = 6;

/** Priority order so highlights (evolutions, festivals) lead the welcome-back. */
const PRIORITY_RANK: Readonly<Record<string, number>> = {
  highlight: 0,
  normal: 1,
  ambient: 2,
};

function rankOf(b: Beat): number {
  return PRIORITY_RANK[b.priority] ?? 1;
}

/** Pick the warmest, most notable beats to phrase, newest-first within priority. */
export function selectWelcomeBeats(beats: readonly Beat[], max = MAX_WELCOME_LETTERS): Beat[] {
  if (max <= 0 || beats.length === 0) return [];
  const sorted = beats.slice().sort((a, b) => {
    const pr = rankOf(a) - rankOf(b);
    if (pr !== 0) return pr;
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return sorted.slice(0, max);
}

function speakerFor(trainer: SimTrainer | undefined, fallbackId: string): SpeakerContext {
  if (!trainer) {
    return {
      trainerId: fallbackId,
      username: 'Someone',
      avatarSpeciesId: 'pikachu',
      personaSummary: 'a friendly neighbor',
      friendshipWithPlayer: 50,
      memorySnippets: [],
    };
  }
  return {
    trainerId: trainer.id,
    username: trainer.username,
    avatarSpeciesId: trainer.avatarSpeciesId,
    personaSummary: `${trainer.playStyle}; ${trainer.personality.traits.join(', ')}`,
    friendshipWithPlayer: trainer.friendshipWithPlayer,
    memorySnippets: trainer.memories
      .filter((m) => m.aboutId === 'player')
      .slice(-2)
      .map((m) => m.summary),
  };
}

/**
 * Compose the warm welcome-back letters from overnight beats. Deterministic given
 * the (beats, trainers, voice-seed). Each letter pairs the ground-truth beat with a
 * single phrased line + the subject's portrait emotion (carried on the VoiceLine).
 */
export async function composeWelcomeBack(
  beats: readonly Beat[],
  trainers: readonly SimTrainer[],
  voice: Voice,
  playerName: string,
  max = MAX_WELCOME_LETTERS,
): Promise<WelcomeLetter[]> {
  const chosen = selectWelcomeBeats(beats, max);
  if (chosen.length === 0) return [];

  const byId = new Map(trainers.map((t) => [t.id, t]));
  const letters: WelcomeLetter[] = [];

  for (const beat of chosen) {
    const speaker = speakerFor(byId.get(beat.subjectTrainerId), beat.subjectTrainerId);
    const res = await voice.generate({
      speakers: [speaker],
      beats: [beat],
      player: { playerName, recentDeeds: [] },
      tokenBudget: 0,
      desiredLineCount: 1,
    });

    const line: VoiceLine =
      res.lines[0] ??
      ({
        trainerId: speaker.trainerId,
        emotion: 'happy',
        text: `${speaker.username} was up to something lovely while you were away.`,
      } satisfies VoiceLine);

    letters.push({ id: beat.id, beat, line });
  }

  return letters;
}
